import { motion } from "framer-motion";
import {
    AlertTriangle,
    CalendarClock,
    CheckCircle2,
    Clipboard,
    ClipboardCheck,
    Eye,
    Link2,
    Loader2,
    MapPin,
    MessageCircle,
    MessageSquare,
    Send,
    Sparkles,
    UserCircle2,
    X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { toE164 } from "../../lib/format";


/**
 * SendMessageModal
 *
 * Reusable modal to compose & send a message to a candidate.
 *
 * Workflow (V1 manual):
 *   1. Load templates + preview the suggested one
 *   2. User picks channel (SMS / WhatsApp), can switch templates, edit text
 *   3. User clicks Copy → text in clipboard, logged in history (status=copied)
 *   4. User clicks "Ouvrir WhatsApp" → deep link to wa.me, logged as sent_externally
 *   5. User clicks "Ouvrir SMS" → deep link sms:... , logged as sent_externally
 */
export default function SendMessageModal({ candidate, defaultCategory, onClose, onSent }) {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [channel, setChannel] = useState("whatsapp");
    const [body, setBody] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [contextUsed, setContextUsed] = useState({});
    const [missingRequired, setMissingRequired] = useState([]);
    const [copiedAt, setCopiedAt] = useState(null);
    const [logging, setLogging] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                const { data } = await api.get("/message-templates");
                setTemplates(data);
                const preferred =
                    data.find((t) => t.is_default && t.category === defaultCategory)
                    || data.find((t) => t.category === defaultCategory)
                    || data.find((t) => t.is_default)
                    || data[0];
                if (preferred) setSelectedTemplateId(preferred.id);
            } catch {
                toast.error("Impossible de charger les modèles");
            } finally {
                setLoadingTemplates(false);
            }
        }
        load();
    }, [defaultCategory]);

    const refreshPreview = useCallback(async (templateId, currentChannel) => {
        if (!templateId) return;
        setPreviewLoading(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/messages/preview`, {
                template_id: templateId,
            });
            const text = currentChannel === "sms" ? data.rendered_sms : data.rendered_whatsapp;
            setBody(text || data.rendered_sms || data.rendered_whatsapp || "");
            setContextUsed(data.context_used || {});
            setMissingRequired(data.missing_required || []);
            setCopiedAt(null);
        } catch {
            toast.error("Erreur d'aperçu");
        } finally {
            setPreviewLoading(false);
        }
    }, [candidate.id]);

    useEffect(() => {
        if (selectedTemplateId) refreshPreview(selectedTemplateId, channel);
    }, [selectedTemplateId, channel, refreshPreview]);

    useEffect(() => {
        function onKey(e) { if (e.key === "Escape") onClose?.(); }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const phoneE164 = candidate.phone ? toE164(candidate.phone) : null;
    const phoneForWa = phoneE164 ? phoneE164.replace(/[^\d]/g, "") : null;

    // === Live validation of the rendered body ===
    // We compare what was rendered with the variables that *were* substituted
    // so the recruiter sees IMMEDIATELY:
    //   - which dynamic values are about to leave the building (highlighted)
    //   - any unrendered placeholder still in the text ({{...}} that didn't match)
    //   - any wrong-domain link (e.g. emergent.host instead of prerec.shariti.com)
    //   - whether the candidate's phone number is set
    const previewIssues = useMemo(() => {
        const issues = [];
        // 1) Unrendered placeholders
        const unrendered = Array.from(body.matchAll(/{{\s*([a-zA-Z0-9_]+)\s*}}/g)).map((m) => m[1]);
        if (unrendered.length > 0) {
            issues.push({
                level: "error",
                title: "Variables non remplies",
                detail: `Le texte contient encore ${unrendered.length} variable${unrendered.length > 1 ? "s" : ""} non remplacée${unrendered.length > 1 ? "s" : ""} : ${[...new Set(unrendered)].map((u) => `{{${u}}}`).join(", ")}. Vérifiez que l'immersion est bien programmée et que les données du candidat sont complètes.`,
            });
        }
        // 2) Wrong-domain magic link
        const linkMatch = body.match(/https?:\/\/[^\s"'<>]+/g) || [];
        const badLink = linkMatch.find((l) => /emergent\.host|emergentagent\.com|preview\.emergentagent/i.test(l));
        if (badLink) {
            issues.push({
                level: "error",
                title: "Lien candidat sur un domaine interne",
                detail: `Le lien "${badLink}" pointe vers un domaine technique d'Emergent au lieu de votre domaine public. Allez dans Paramètres entreprise → URL publique de PREREC → renseignez https://prerec.shariti.com.`,
            });
        }
        // 3) Phone missing
        if (!phoneE164) {
            issues.push({
                level: "warning",
                title: "Téléphone du candidat manquant",
                detail: "Aucun numéro n'est enregistré pour ce candidat — vous ne pourrez pas ouvrir SMS ni WhatsApp directement. Vous pouvez toujours copier le message.",
            });
        }
        return issues;
    }, [body, phoneE164]);

    // === Variables actually used in the rendered body (for the badge row) ===
    const rezonant = useMemo(() => {
        const used = [];
        const ctx = contextUsed || {};
        function maybe(key, label, icon) {
            const v = ctx[key];
            if (v && String(v).trim() && body.includes(v)) {
                used.push({ key, label, value: String(v), icon });
            }
        }
        maybe("prenom", "Prénom", UserCircle2);
        maybe("date_heure_immersion", "Date & heure", CalendarClock);
        maybe("lieu_immersion", "Lieu", MapPin);
        maybe("lien_confirmation", "Lien confirmation", Link2);
        return used;
    }, [body, contextUsed]);

    async function logMessage(status) {
        if (!body.trim() || logging) return;
        setLogging(true);
        const tpl = templates.find((t) => t.id === selectedTemplateId);
        try {
            await api.post(`/candidates/${candidate.id}/messages`, {
                template_id: tpl?.is_built_in ? null : selectedTemplateId,
                template_name: tpl?.name,
                category: tpl?.category || "custom",
                channel,
                body,
                status,
            });
            onSent?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible d'enregistrer dans l'historique");
        } finally {
            setLogging(false);
        }
    }

    async function handleCopy() {
        if (missingRequired.length > 0) {
            toast.error(`Impossible de copier — ${missingRequired.length} information${missingRequired.length > 1 ? "s manquantes" : " manquante"}`);
            return;
        }
        let copied = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(body);
                copied = true;
            }
        } catch { /* fallthrough */ }
        if (!copied) {
            try {
                const ta = document.createElement("textarea");
                ta.value = body;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.top = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                copied = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch { copied = false; }
        }
        if (copied) {
            setCopiedAt(Date.now());
            toast.success("Message copié");
            await logMessage("copied");
        } else {
            toast.error("Sélectionnez le texte et Ctrl+C");
        }
    }

    function handleOpenWhatsApp() {
        if (missingRequired.length > 0) {
            toast.error(`Impossible d'envoyer — ${missingRequired.length} information${missingRequired.length > 1 ? "s manquantes" : " manquante"}`);
            return;
        }
        if (!phoneForWa) {
            toast.error("Numéro de téléphone du candidat manquant");
            return;
        }
        const url = `https://wa.me/${phoneForWa}?text=${encodeURIComponent(body)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        logMessage("sent_externally");
        toast.success("WhatsApp ouvert");
    }

    function handleOpenSms() {
        if (missingRequired.length > 0) {
            toast.error(`Impossible d'envoyer — ${missingRequired.length} information${missingRequired.length > 1 ? "s manquantes" : " manquante"}`);
            return;
        }
        if (!phoneE164) {
            toast.error("Numéro de téléphone du candidat manquant");
            return;
        }
        const url = `sms:${phoneE164}?&body=${encodeURIComponent(body)}`;
        window.location.href = url;
        logMessage("sent_externally");
    }

    const currentTpl = templates.find((t) => t.id === selectedTemplateId);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            data-testid="send-message-modal"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 max-w-3xl w-full max-h-[92vh] flex flex-col my-8"
            >
                <div className="flex items-start justify-between gap-4 mb-5 shrink-0">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <Sparkles className="h-3 w-3" /> Message candidat
                        </div>
                        <h2 className="mt-3 text-xl font-black tracking-tight text-shariti-black">
                            {candidate.first_name} {candidate.last_name}
                        </h2>
                        <p className="mt-1 text-sm font-medium text-slate-500">
                            {phoneE164 || "—"} · Choisissez le modèle, ajustez le texte, copiez ou ouvrez WhatsApp.
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition shrink-0">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-3 shrink-0 mb-4">
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Modèle</span>
                        {loadingTemplates ? (
                            <div className="input-shariti flex items-center gap-2 text-slate-400">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
                            </div>
                        ) : (
                            <select
                                value={selectedTemplateId || ""}
                                onChange={(e) => setSelectedTemplateId(e.target.value)}
                                data-testid="message-template-select"
                                className="input-shariti"
                            >
                                {templates.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.is_default ? "★ " : ""}{t.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {currentTpl?.description && (
                            <p className="text-[10px] font-medium text-slate-400 mt-1 italic">
                                {currentTpl.description}
                            </p>
                        )}
                    </div>
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Canal</span>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setChannel("whatsapp")}
                                data-testid="message-channel-whatsapp"
                                className={`inline-flex items-center justify-center gap-1.5 h-10 rounded-xl text-xs font-extrabold transition ${
                                    channel === "whatsapp"
                                        ? "bg-emerald-500 text-white shadow-btn"
                                        : "bg-white border border-shariti-gray text-slate-500 hover:border-emerald-500 hover:text-emerald-600"
                                }`}
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp
                            </button>
                            <button
                                type="button"
                                onClick={() => setChannel("sms")}
                                data-testid="message-channel-sms"
                                className={`inline-flex items-center justify-center gap-1.5 h-10 rounded-xl text-xs font-extrabold transition ${
                                    channel === "sms"
                                        ? "bg-shariti-orange text-white shadow-btn"
                                        : "bg-white border border-shariti-gray text-slate-500 hover:border-shariti-orange hover:text-shariti-orange"
                                }`}
                            >
                                <MessageSquare className="h-4 w-4" /> SMS
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3">
                    {/* === HARD blocker: required data missing === */}
                    {missingRequired.length > 0 && (
                        <div
                            data-testid="missing-required-banner"
                            className="rounded-2xl border-2 border-shariti-red/50 bg-red-50 p-4"
                        >
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-shariti-red shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-extrabold text-shariti-red">
                                        Impossible d'envoyer ce message
                                    </div>
                                    <div className="text-xs font-semibold text-shariti-red/85 mt-1">
                                        Ce template attend {missingRequired.length} information{missingRequired.length > 1 ? "s" : ""} qui n'{missingRequired.length > 1 ? "ont" : "a"} pas encore été renseignée{missingRequired.length > 1 ? "s" : ""}. Le candidat recevrait un message incomplet avec des trous (ex. <em>« joignez directement * au * »</em>).
                                    </div>
                                    <ul className="mt-3 space-y-1.5">
                                        {missingRequired.map((m) => (
                                            <li
                                                key={m.key}
                                                data-testid={`missing-required-${m.key}`}
                                                className="flex items-start gap-2 text-[12px] font-bold text-shariti-red"
                                            >
                                                <span className="h-1.5 w-1.5 mt-1.5 rounded-full bg-shariti-red shrink-0" />
                                                {m.label}
                                                {m.source === "schedule" && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white border border-shariti-red/30 text-shariti-red/85">
                                                        À programmer
                                                    </span>
                                                )}
                                                {m.source === "referent_profile" && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white border border-shariti-red/30 text-shariti-red/85">
                                                        Profil référent
                                                    </span>
                                                )}
                                                {m.source === "settings" && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white border border-shariti-red/30 text-shariti-red/85">
                                                        Paramètres entreprise
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {missingRequired.some((m) => m.source === "schedule") && (
                                            <a
                                                href={`/candidats/${candidate.id}`}
                                                data-testid="missing-required-go-schedule"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-shariti-red text-white text-[11px] font-extrabold hover:bg-red-700 transition"
                                            >
                                                Compléter la programmation →
                                            </a>
                                        )}
                                        {missingRequired.some((m) => m.source === "referent_profile") && (
                                            <a
                                                href="/utilisateurs"
                                                data-testid="missing-required-go-referent"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-shariti-red border border-shariti-red/40 text-[11px] font-extrabold hover:bg-red-50 transition"
                                            >
                                                Ouvrir le profil référent →
                                            </a>
                                        )}
                                        {missingRequired.some((m) => m.source === "settings") && (
                                            <a
                                                href="/parametres-entreprise"
                                                data-testid="missing-required-go-settings"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-shariti-red border border-shariti-red/40 text-[11px] font-extrabold hover:bg-red-50 transition"
                                            >
                                                Compléter les paramètres →
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* === Preview banner: what the candidate WILL receive === */}
                    {body && !previewLoading && (
                        <div
                            data-testid="message-preview-banner"
                            className={`rounded-2xl border-2 p-3 md:p-4 ${
                                previewIssues.some((i) => i.level === "error")
                                    ? "border-shariti-red/40 bg-red-50/40"
                                    : "border-shariti-orange/30 bg-shariti-orange-soft/30"
                            }`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Eye className="h-4 w-4 text-shariti-orange shrink-0" />
                                <span className="text-[11px] font-extrabold uppercase tracking-widest text-shariti-orange">
                                    Aperçu exact du message qui sera envoyé à {candidate.first_name}
                                </span>
                            </div>
                            {/* Substituted variables row */}
                            {rezonant.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2.5" data-testid="preview-variables-row">
                                    {rezonant.map((r) => {
                                        const Icon = r.icon;
                                        return (
                                            <span
                                                key={r.key}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-shariti-orange/30 text-[11px] font-bold text-shariti-black"
                                                data-testid={`preview-var-${r.key}`}
                                                title={`Variable {{${r.key}}}`}
                                            >
                                                <Icon className="h-3 w-3 text-shariti-orange shrink-0" />
                                                <span className="font-extrabold text-shariti-orange">{r.label} :</span>
                                                <span className="truncate max-w-[180px]" title={r.value}>{r.value}</span>
                                                <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Issue list */}
                            {previewIssues.length > 0 && (
                                <div className="space-y-1.5" data-testid="preview-issues">
                                    {previewIssues.map((issue, idx) => (
                                        <div
                                            key={idx}
                                            data-testid={`preview-issue-${issue.level}`}
                                            className={`flex items-start gap-2 text-[12px] font-medium rounded-xl px-2.5 py-2 border ${
                                                issue.level === "error"
                                                    ? "bg-white border-shariti-red/30 text-shariti-red"
                                                    : "bg-white border-amber-300 text-amber-800"
                                            }`}
                                        >
                                            <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${
                                                issue.level === "error" ? "text-shariti-red" : "text-amber-600"
                                            }`} />
                                            <div>
                                                <div className="font-extrabold">{issue.title}</div>
                                                <div className="text-[11px] font-medium opacity-85">{issue.detail}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {previewIssues.length === 0 && rezonant.length > 0 && (
                                <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700">
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    Tout est bon — vérifiez l'orthographe ci-dessous puis envoyez.
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                Texte — éditable
                            </span>
                            <span className="text-[10px] font-medium text-slate-400">
                                {body.length} caractères{channel === "sms" && body.length > 160 ? ` · ${Math.ceil(body.length / 160)} SMS` : ""}
                            </span>
                        </div>
                        <textarea
                            value={body}
                            onChange={(e) => { setBody(e.target.value); setCopiedAt(null); }}
                            data-testid="message-body"
                            rows={Math.min(20, Math.max(8, (body.match(/\n/g) || []).length + 4))}
                            className="input-shariti font-mono text-[13px] leading-relaxed resize-y w-full min-h-[200px]"
                            placeholder={previewLoading ? "Génération en cours…" : "Le message s'affiche ici…"}
                        />
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-end gap-2 shrink-0 border-t border-shariti-gray pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-4 h-10 font-bold text-xs transition"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={handleCopy}
                        disabled={!body.trim() || logging || missingRequired.length > 0}
                        data-testid="message-copy"
                        title={missingRequired.length > 0 ? "Compléter les informations manquantes avant de copier" : undefined}
                        className={`inline-flex items-center gap-2 rounded-full px-5 h-10 font-extrabold text-xs transition shadow-btn disabled:opacity-50 disabled:cursor-not-allowed ${
                            copiedAt ? "bg-emerald-500 text-white" : "bg-shariti-black text-white hover:bg-slate-800"
                        }`}
                    >
                        {copiedAt ? <ClipboardCheck className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
                        {copiedAt ? "Copié !" : "Copier le texte"}
                    </button>
                    {channel === "whatsapp" && phoneForWa && (
                        <button
                            type="button"
                            onClick={handleOpenWhatsApp}
                            disabled={!body.trim() || logging || missingRequired.length > 0}
                            data-testid="message-open-whatsapp"
                            title={missingRequired.length > 0 ? "Compléter les informations manquantes avant d'envoyer" : undefined}
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-5 h-10 font-extrabold text-xs transition shadow-btn disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Send className="h-4 w-4" /> Ouvrir WhatsApp
                        </button>
                    )}
                    {channel === "sms" && phoneE164 && (
                        <button
                            type="button"
                            onClick={handleOpenSms}
                            disabled={!body.trim() || logging || missingRequired.length > 0}
                            data-testid="message-open-sms"
                            title={missingRequired.length > 0 ? "Compléter les informations manquantes avant d'envoyer" : undefined}
                            className="inline-flex items-center gap-2 rounded-full bg-shariti-orange hover:bg-[#d66528] text-white px-5 h-10 font-extrabold text-xs transition shadow-btn disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Send className="h-4 w-4" /> Ouvrir SMS
                        </button>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
}
